///src/components/CreateCampaignDialog/validations.ts
import type { CampaignFormData } from './createCampaignType'

export interface ValidationResult {
  isValid: boolean
  errors: Record<string, string>
}

// Re-export CampaignFormData to ensure a single source of truth across the dialog
export type { CampaignFormData }

/**
 * Validates Step 1 - Campaign Info
 */
export const validateStep1 = (formData: Partial<CampaignFormData>): ValidationResult => {
  const errors: Record<string, string> = {}

  if (!formData?.name) {
    errors.name = 'Name is required'
  } else if (formData.name.length < 2) {
    errors.name = 'Name must be at least 2 characters long'
  }
  if (!formData?.product) {
    errors.product = 'Product is required'
  } else if (formData.product.length < 2) {
    errors.product = 'Product must be at least 2 characters long'
  }
  if (!formData?.contentFormat) {
    errors.contentFormat = 'Content format is required'
  } else if (formData.contentFormat.length < 2) {
    errors.contentFormat = 'Content format must be at least 2 characters long'
  }
  if (!formData?.description) {
    errors.description = 'Description is required'
  } else if (formData.description.length < 2) {
    errors.description = 'Description must be at least 2 characters long'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validates Step 2 - General Info
 */
export const validateStep2 = (formData: Partial<CampaignFormData>): ValidationResult => {
  const errors: Record<string, string> = {}

  if (!formData?.language) {
    errors.language = 'Select Language'
  }

  if (!formData?.gender) {
    errors.gender = 'Select Gender'
  }

  if (!formData?.age) {
    errors.age = 'Select Age'
  }

  if (formData?.autoStartEnabled) {
    if (!formData?.startDate) {
      errors.startDate = 'Start date is required'
    }

    if (!formData?.endDate) {
      errors.endDate = 'End date is required'
    }

    if (formData?.startDate && formData?.endDate) {
      const start = new Date(formData.startDate)
      const end = new Date(formData.endDate)
      if (end <= start) {
        errors.endDate = 'End date must be after start date'
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validates Step 3 - Influencers
 */
export const validateStep3 = (formData: Partial<CampaignFormData>): ValidationResult => {
  const errors: Record<string, string> = {}
  const MAX_FOLLOWERS = 2147483640

  if (!formData?.industries || formData.industries.length === 0) {
    errors.industries = 'Select at least one industry'
  }

  // Validate follower counts for each platform
  const platforms = [
    { required: 'instagramFollowersRequired', count: 'instagramFollowersCount' },
    { required: 'youtubeFollowersRequired', count: 'youtubeFollowersCount' },
    { required: 'tiktokFollowersRequired', count: 'tiktokFollowersCount' },
  ] as const

  for (const { required, count } of platforms) {
    if (formData?.[required]) {
      if (!formData?.[count] || Number(formData[count]) <= 0) {
        errors[count] = 'Minimum followers count is required'
      } else if (Number(formData[count]) > MAX_FOLLOWERS) {
        errors[count] = `Maximum followers count is ${MAX_FOLLOWERS.toLocaleString()}`
      }
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}
/**
 * Validates Step 4 - Upload Image
 */
export const validateStep4 = (formData: Partial<CampaignFormData>): ValidationResult => {
  const errors: Record<string, string> = {}

  if (!formData?.coverImageFile) {
    errors.coverImageFile = 'Cover image is required'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Validates Step 5 - Buddget
 */
export const validateStep5 = (formData: Partial<CampaignFormData>): ValidationResult => {
  const errors: Record<string, string> = {}

  const payoutEnabled = Array.isArray(formData?.rewards) && formData.rewards.includes('Payout')

  if (payoutEnabled) {
    if (!formData?.budget?.currency) {
      errors.currency = 'Select a currency'
    }

    if (!formData.budget?.value) {
      errors.value = 'Campaign budget value is required'
    }
  }
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Main validation function that routes to the appropriate step validator
 */
export const validateStep = (step: number, formData: Partial<CampaignFormData>): ValidationResult => {
  switch (step) {
    case 1:
      return validateStep1(formData)
    case 2:
      return validateStep2(formData)
    case 3:
      return validateStep3(formData)
    case 4:
      return validateStep4(formData)
    default:
      return { isValid: true, errors: {} }
  }
}
